import { Box, Typography, useTheme } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DEFAULT_QUESTION } from "./constants";
import { QuestionnaireItemFreeTextSize, QuestionnaireItemType } from "../../_helpers";
import { useTranslation } from "react-i18next";
import RenderQuestion from "./QuestionFormView/RenderQuestion";
import QuestionnaireQuestion from "./QuestionnaireQuestion";
import QuestionnaireFormTitleDescription from "./QuestionnaireFormTitleDescription";

const QuestionnaireQuestionSection = ({
  section,
  sectionIndex,
  totalSections,
  updateSectionTitle,
  updateQuestion,
  setSections,
  activeToolbarKey,
  setActiveToolbarKey,
  handleAddQuestion,
  questionFormErrors,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(section.questions);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setSections((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex ? { ...sec, questions: reordered } : sec
      )
    );
    setActiveToolbarKey(`${sectionIndex}-${result.destination.index}`)
  };

  const handleAddSection = (e) => {
    e.stopPropagation();
    const defaultQuestion = {
      ...DEFAULT_QUESTION.questions[0],
      options: [{ option: t("questionnaire.form.optionWithNo", { number: 1 }) }]
    };

    const newSection = {
      ...DEFAULT_QUESTION,
      questions: [defaultQuestion],
    };

    setSections((prev) => [...prev, newSection]);

    setActiveToolbarKey(`${totalSections}-0`);
  };


  const handleDeleteQuestion = (questionIndex) => {
    setSections((prev) =>
      prev.map((sec, i) => {
        if (i !== sectionIndex) return sec;
        const newQuestions = sec.questions.filter((_, qi) => qi !== questionIndex);
        return { ...sec, questions: newQuestions };
      })
    );
    const newQuestionIndexKey = questionIndex - 1
    setActiveToolbarKey(`${sectionIndex}-${newQuestionIndexKey >= 0 ? newQuestionIndexKey : questionIndex}`);
  };

  const handleDuplicateQuestion = (questionIndex) => {
    setSections((prev) =>
      prev.map((sec, i) => {
        if (i !== sectionIndex) return sec;
        const q = sec.questions[questionIndex];
        const newQuestions = [...sec.questions];
        const duplicated = { ...q, order: questionIndex + 1 };
        newQuestions.splice(questionIndex + 1, 0, duplicated);
        return { ...sec, questions: newQuestions };
      })
    );
    setActiveToolbarKey(`${sectionIndex}-${section?.questions?.length}`);
  };

  const handleDeleteSection = () => {
    setSections((prev) => {
      return prev.filter((sec, index) => index !== sectionIndex)
    }
    );
    setActiveToolbarKey(`${sectionIndex - 1}`);
  };


  const handleDuplicateSection = () => {
    setSections((prev) => {
      const duplicated = { ...prev[sectionIndex], order: sectionIndex + 1 };
      prev.splice(sectionIndex + 1, 0, duplicated);
      return [...prev]
    }
    );
    setActiveToolbarKey(totalSections)
  };

  const handleAddFreeText = (e) => {
    const newBlock = {
      ...DEFAULT_QUESTION.questions[0],
      type: QuestionnaireItemType.FreeText,
      freeTextSize: QuestionnaireItemFreeTextSize.Medium,
      text: "",
      options: [],
    };
    setSections((prev) => {
      const updatedSections = prev.map((sec, i) =>
        i === sectionIndex ? { ...sec, questions: [...sec.questions, newBlock] } : sec
      );
      return updatedSections;
    });

    setActiveToolbarKey(`${sectionIndex}-${section?.questions?.length}`);
  };

  const handleFocusOnTitle = () => {
    setActiveToolbarKey(sectionIndex);
  };


  return (
    <Box sx={{ mb: 4, position: "relative" }} px={1}>
      {totalSections > 1 && (
        <Typography
          sx={{
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            px: 1.5,
            py: 0.5,
            borderTopLeftRadius: 6,
            borderTopRightRadius: 6,
            fontSize: 13,
            display: "inline-block",
          }}
        >
          {t("questionnaire.form.section")} {sectionIndex + 1}{" "}
          {t("datatable.label.pagination.displayRows")} {totalSections}
        </Typography>
      )}

      <QuestionnaireFormTitleDescription
        sectionIndex={sectionIndex}
        formInfo={{ title: section.title, description: section.description }}
        updateFormTitle={(patch) => updateSectionTitle(sectionIndex, patch)}
        isMainForm={sectionIndex === 0}
        onFocusTitle={handleFocusOnTitle}
        handleAddQuestion={(e) => handleAddQuestion(e, { sectionIndex, questionIndex: -1 })}
        handleAddSection={handleAddSection}
        handleAddFreeText={handleAddFreeText}
        activeToolbarKey={activeToolbarKey}
        error={questionFormErrors?.[sectionIndex]}
        handleDuplicateSection={handleDuplicateSection}
        handleDeleteSection={handleDeleteSection}
      />

      {/* Questions */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`section-${sectionIndex}`}>
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {section.questions.map((q, questionIndex) => (
                <Draggable
                  key={`${sectionIndex}-${questionIndex}`}
                  draggableId={`${sectionIndex}-${questionIndex}`}
                  index={questionIndex}
                >
                  {(provided, snapshot) => (
                    <Box
                      id={`question-${sectionIndex}-${questionIndex}`}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={() =>
                        setActiveToolbarKey(`${sectionIndex}-${questionIndex}`)
                      }
                      sx={{
                        ...provided.draggableProps.style,
                        opacity: snapshot.isDragging ? 0.7 : 1,
                        cursor: "pointer",
                        paddingBottom:
                          activeToolbarKey === `${sectionIndex}-${questionIndex}`
                            ? { xs: "50px", md: "0px" }
                            : "0px"

                      }}
                    >
                      {activeToolbarKey === `${sectionIndex}-${questionIndex}` ? (
                        <QuestionnaireQuestion
                          question={q}
                          sectionIndex={sectionIndex}
                          questionIndex={questionIndex}
                          updateQuestion={updateQuestion}
                          setSections={setSections}
                          deleteQuestion={(e) => {
                            e.stopPropagation()
                            handleDeleteQuestion(questionIndex)
                          }
                          }
                          duplicateQuestion={(e) => {
                            e.stopPropagation()
                            handleDuplicateQuestion(questionIndex)

                          }
                          }
                          handleAddSection={handleAddSection}
                          handleAddFreeText={handleAddFreeText}
                          activeToolbarKey={activeToolbarKey}
                          setActiveToolbarKey={setActiveToolbarKey}
                          handleAddQuestion={(e) => handleAddQuestion(e, { sectionIndex, questionIndex })}
                          section={section}
                          error={questionFormErrors?.[`${sectionIndex}-${questionIndex}`]}
                        />
                      ) : (
                        <RenderQuestion
                          q={q}
                          isDisabled
                          error={questionFormErrors?.[`${sectionIndex}-${questionIndex}`]}
                        />
                      )}
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>


      {totalSections > 1 && (
        <Typography variant="body2" sx={{ ml: 1, pt: 2 }}>
          {t("questionnaire.form.section.after")} {sectionIndex + 1}{" "}
          <strong>
            {t("questionnaire.form.section.continueToNext")}
          </strong>
        </Typography>
      )}
    </Box>
  );
};

export default QuestionnaireQuestionSection;
